import { watch } from "vue";
import { RouteRecordRaw } from "vue-router";
import {
  NavMenuItem,
  registerNavMenuItem,
  BreadcrumbParam,
  addBreadCrumbs,
} from "@/core/layout";
import { addRoutes } from "@/core/router";
import { isDataStorePlusEnabled } from "@/organization/services/organization-datastore-plus.service";
import { isUserLoggedIn } from "@/core/auth";

const dataStorePlusRoute: RouteRecordRaw = {
  path: "/data-store-plus",
  name: "dataStorePlus",
  component: () => import("@/datastore-plus/pages/DataStorePlus.vue"),
  meta: {
    authorize: ["DATA_STORE_PLUS.PAGE.VIEW"],
  },
};

addRoutes([dataStorePlusRoute]);

const dataStorePlusItem: NavMenuItem = {
  iconName: "database",
  label: "Data Store +",
  name: "dataStorePlus",
  targetRoute: {
    path: "/data-store-plus",
    name: "dataStorePlus",
    component: () => import("@/datastore-plus/pages/DataStorePlus.vue"),
    meta: {
      authorize: ["DATA_STORE_PLUS.PAGE.VIEW"],
    },
  },
  rank: 55,
  isParent: false,
  hasParent: false,
};

let menuItemRegistered = false;

// Create the datastore menu item with conditional visibility logic.
// isDataStorePlusEnabled is an authenticated, organization-scoped call, so it is
// never started before login — a bootstrap read would 401 and answer "off".
// The guard keeps refreshDataStorePlusMenu below able to register an organization
// that has just been enabled, while making a second register a no-op:
// registerNavMenuItem throws on a duplicate rank.
const createDataStorePlusMenuItem = async () => {
  if (menuItemRegistered) {
    return;
  }
  try {
    const isEnabled = await isDataStorePlusEnabled();
    if (isEnabled && !menuItemRegistered) {
      menuItemRegistered = true;
      // Register the DataStore menu item under Connectors
      registerNavMenuItem(dataStorePlusItem);
    }
  } catch (error) {
    console.warn("Failed to check Data Store Plus visibility:", error);
  }
};

// Registered on the login transition rather than once at module evaluation:
// feature packages are imported a single time per page load (core/vue/startup.ts
// → loadFeaturePackages), and a cold open lands on the login page with no
// session, so a one-shot isUserLoggedIn check there would leave the menu missing
// for the whole session that follows.
watch(
  isUserLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      createDataStorePlusMenuItem();
    }
  },
  { immediate: true }
);

const dataStorePlusBreadcrumb: BreadcrumbParam = {
  routeName: "dataStorePlus",
  items: [
    {
      labelKey: "Auth.Home",
      route: "/dashboard",
      disabled: false,
      isTranslated: true,
      uniqueLabel: "",
    },
    {
      labelKey: "DataStorePlus.DataStorePlus",
      route: "/data-store-plus",
      disabled: true,
      isTranslated: true,
      uniqueLabel: "",
    },
  ],
};

addBreadCrumbs([dataStorePlusBreadcrumb]);

// Export function to refresh menu (this can be called from other modules)
export const refreshDataStorePlusMenu = () => {
  // Re-initialize to properly handle both enable and disable scenarios
  createDataStorePlusMenuItem();
};

// Export function for external use - this will be called when organization changes
export const initializeDataStorePlusMenu = () => {
  createDataStorePlusMenuItem();
};
